// Libraries
import { applyMiddleware, compose, createStore } from 'redux';
import {
  fetchThreadSaga,
  fetchUsersSaga,
  selectUserSaga
} from 'actions/communication/messages/sagas';
import { persistReducer, persistStore } from 'redux-persist';
import {
  searchUsersSaga,
  sendMessageSaga
} from 'actions/communication/compose/composeSagas';
import Raven from 'raven-js';
import { alertSaga } from 'actions/travelSecurity/alerts/actions';
import { changeLanguageSaga } from 'actions/language/languageSagas';
import createRavenMiddleware from 'raven-for-redux';
import createSagaMiddleware from 'redux-saga';
import { fetchUsersSaga as fetchAccountsSaga } from 'actions/accounts/users/usersActions';
import { fetchGroupsSaga } from 'actions/accounts/groups/groupsActions';
import { fetchPlatformUsageDataSaga } from 'actions/platformUsageAnalytics/platformUsageAnalytics';
import { fetchSHAWAnalyticsDataSaga } from 'actions/SHAWAnalytics/actions';
import { fetchCallCenterUtilizationSaga } from 'actions/callCenterUtilization/actions';
import { initSaga } from 'actions/ui/init/initSagas';
import isLocalHost from './registerServiceWorker';
import { loginSaga } from 'actions/auth/login';
import { passwordResetSaga } from 'actions/auth/password';
import persistConfig from './persist';
import { responsiveStoreEnhancer } from 'redux-responsive';
import rootReducer from 'actions/rootReducer';
import { routeSaga } from 'actions/routes/routeSagas';
import { routerMiddleware } from 'react-router-redux';
import { runOnboardingSaga } from 'actions/onboarding/onboardingSagas';
import { sendFeedbackSaga } from 'actions/communication/feedback/feedbackSagas';

export const combinedSagas = () => [
  initSaga,
  loginSaga,
  passwordResetSaga,
  fetchPlatformUsageDataSaga,
  fetchSHAWAnalyticsDataSaga,
  fetchCallCenterUtilizationSaga,
  fetchUsersSaga,
  fetchAccountsSaga,
  fetchGroupsSaga,
  fetchThreadSaga,
  selectUserSaga,
  changeLanguageSaga,
  searchUsersSaga,
  sendMessageSaga,
  sendFeedbackSaga,
  alertSaga,
  routeSaga,
  runOnboardingSaga
];

export const registerSagas = (sagaMiddleware, sagas) =>
  sagas.forEach(sagaMiddleware.run);
/**
 * Configures the initialization of a React store, applying middleware, and interfacing with
 * tooling.
 *
 * @param  {State} initialState State object generated from indexed reducers.
 * @returns {Store} Generates an enhanced Redux Store.
 */
export const configureStore = (initialState, history) => {
  // Create saga middleware
  const sagaMiddleware = createSagaMiddleware();
  const routeMiddleware = routerMiddleware(history);
  // Create a function that can apply the saga middleware to a StoreCreator
  const sagaStoreEnhancer = compose(
    responsiveStoreEnhancer,
    applyMiddleware(sagaMiddleware, routeMiddleware)
  );

  // Create a function that can create a store with the new middleware
  const createStoreWithMiddleWare = sagaStoreEnhancer(createStore);

  // Raven Setup
  let store;
  Raven.config(window.config.REACT_APP_SENTRY).install();
  const raven = createRavenMiddleware(Raven);

  // Redux-Persist Setup
  const persistedReducer = persistReducer(persistConfig, rootReducer);

  // Determine whether debug mode should be attached
  if (isLocalHost) {
    // React performance devtools
    const reduxDevTools = window.__REDUX_DEVTOOLS_EXTENSION__;
    const debug =
      typeof reduxDevTools === 'function' ? reduxDevTools() : (a) => a;
    store = createStoreWithMiddleWare(
      persistedReducer,
      initialState,
      debug,
      raven
    );
  } else {
    store = createStoreWithMiddleWare(persistedReducer, initialState, raven);
  }
  // Register all sagas with middleware after persistor has rehydrated state
  const persistor = persistStore(store, null, () =>
    registerSagas(sagaMiddleware, combinedSagas())
  );
  return { store, persistor };
};

export default configureStore;



// WEBPACK FOOTER //
// ./src/lib/configureStore.js